// #region imports

// --- Angular
import { HttpClient, HttpErrorResponse } from "@angular/common/http";

// --- RxJs
import { Observable, catchError, combineLatest, forkJoin, map, of } from "rxjs";

// --- 3rd Party
import { JL } from 'jsnlog';
import { retryBackoff } from "backoff-rxjs";
import { TranslateService } from "@ngx-translate/core";

// --- HS Code
import { environment } from "../../../../environments/environment";
import { ApplicationError, ErrorMessage, GroupModel, RespondentModel } from "../../../models";
import { RemoteCommandOfT } from "./remote-command";
import { AppService } from "..";
import { Inject } from "@angular/core";

// #endregion

export class DeleteGroupCommand extends RemoteCommandOfT<GroupModel[]> {
  private readonly _apiUri: string = environment.apiConfig.surveyApiUrl + environment.apiConfig.participantsGroupsApi;
  private readonly _groups: GroupModel[];
  private readonly _group: GroupModel;

  constructor(
    private _logger: JL.JSNLog,
    groups: GroupModel[],
    group: GroupModel,
    _appService: AppService,
    _httpClient: HttpClient,
    _translateService: TranslateService
  ) {
    super(_appService, _httpClient, _translateService);
    this._groups = groups;
    this._group = group;
  }

  override execute(): void {
    if (this._executionStatus !== this._COMMAND_STATE_NOT_EXECUTED) return;

    this._executionStatus = this._COMMAND_STATE_EXECUTING;

    const url = this._apiUri + '/' + this._group.id;

    if (this._group.isSet) {
      this._appService.setLoading(true);

      this._httpClient.delete(url)
        .pipe(
          retryBackoff(environment.retryConfig),
          map(() => true),
          catchError(this._deleteError.bind(this))
        ).subscribe((isSuccess: boolean) => {
          if (isSuccess) {
            const deletedGroupIndex = this._groups.findIndex(g => g.index === this._group.index);
            this._groups.splice(deletedGroupIndex, 1);
            this._groups.forEach(g => {
              if (g.index >= this._group.index) {
                g.index--;
              }
            });
            this._commandResult$.next(this._groups);
            this._executionStatus = this._COMMAND_STATE_EXECUTED;
          } else{
            this._executionStatus = this._COMMAND_STATE_NOT_EXECUTED;
          }

          this._appService.setLoading(false);
        });
    } else {
      const deletedGroupIndex = this._groups.findIndex(g => g.index === this._group.index);
      this._groups.splice(deletedGroupIndex, 1);
      this._groups.forEach(g => {
        if (g.index >= this._group.index) {
          g.index--;
        }
      });
      this._commandResult$.next(this._groups);
      this._executionStatus = this._COMMAND_STATE_EXECUTED;
    }
  }

  override undo(): void {
    if (this._executionStatus !== this._COMMAND_STATE_EXECUTED) return;

    this._executionStatus = this._COMMAND_STATE_EXECUTING;

    const url = this._apiUri;

    const groupsToAdd = new Array<GroupModel>();
    const groupToAdd = structuredClone(this._group);
    groupToAdd.respondents.splice(0, groupToAdd.respondents.length);
    groupsToAdd.push(groupToAdd);

    if (groupToAdd.isSet) {
      this._appService.setLoading(true);

      this._httpClient.post<GroupModel[]>(url, groupsToAdd)
        .pipe(
          retryBackoff(environment.retryConfig),
          catchError(this._postError.bind(this))
        ).subscribe((newGroups: GroupModel[]) => {
          if (this._group.errorMessage) {
            this._appService.setLoading(false);
            this._commandResult$.next(this._groups);
            this._executionStatus = this._COMMAND_STATE_EXECUTED;
            return;
          }

          if (!newGroups) throw new Error('Assertion failed');

          const newGroup: GroupModel | undefined = newGroups.find(g => g.index === groupToAdd.index);
          if (!newGroup) throw new Error('Assertion failed');

          const restoredGroups = this._setGroupProperties(groupToAdd, newGroup);

          const url = environment.apiConfig.surveyApiUrl + environment.apiConfig.respondentsApi;
          const addRespondents: Observable<RespondentModel>[] = [];

          this._group.respondents.filter(r => r.isSet).forEach(respondent => {
            respondent.groupId = newGroup.id;
            addRespondents.push(this._httpClient.post<RespondentModel>(url, respondent));
          });

          if (addRespondents.length === 0) {
            this._appService.setLoading(false);
            this._groups.splice(0, this._groups.length, ...restoredGroups);
            this._commandResult$.next(this._groups);
            this._executionStatus = this._COMMAND_STATE_NOT_EXECUTED;
            return;
          }

          combineLatest(addRespondents)
            .pipe(catchError(this._respondentsError.bind(this)))
            .subscribe((respondents: RespondentModel[]) => {
              this._appService.setLoading(false);

              const restoredGroup = restoredGroups.find(g => g.id === newGroup.id);

              if (respondents) {
                restoredGroup!.respondents = respondents.concat(this._group.respondents.filter(r => !r.isSet));
              }

              this._groups.splice(0, this._groups.length, ...restoredGroups);
              this._commandResult$.next(this._groups);
              this._executionStatus = this._COMMAND_STATE_NOT_EXECUTED;
            });
        });
    }
  }

  private _setGroupProperties(groupToAdd: GroupModel, newGroup: GroupModel): GroupModel[] {
    groupToAdd.id = newGroup.id;
    groupToAdd.isSet = true;
    groupToAdd.isValid = true;

    // fill with placeholder respondents
    for (let i = 0; i < groupToAdd.numberOfRespondents; i++) {
      groupToAdd.respondents.push(this._createEmptyRespondentModel(groupToAdd.id, i + 1));
    }

    // modify the groups list
    const newGroups = structuredClone(this._groups);
    newGroups.forEach(g => {
      if (g.index >= newGroup.index) g.index++;
      if (groupToAdd.isActiveGroup) g.isActiveGroup = false;
    });

    newGroups.splice(groupToAdd.index - 1, 1, groupToAdd);
    return newGroups;
  }

  private _respondentsError(_respondentsError: any, caught: Observable<RespondentModel[]>): Observable<RespondentModel[]> {
    const error = {
      errorMessage: this._translateService.instant('errorMessageGenericBackend'),
    } as ApplicationError;
    this._logger().error({ errorMessage: 'An error occurred attempting to restore a deleted respondent.', error: _respondentsError});
    this._appService.setLastError(error);
    return caught;
  }

  private _deleteError(err: any): Observable<boolean> {

    const errorMessage: string = this._translateService.instant('errorMessageGenericBackend');


    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'DeleteGroupCommand',
      false,
      err
    );

    this._logger().error({ errorMessage: 'An error occurred attempting to deleted a group.', error: err});
    this._appService.setLastError(error);
    return of(false);
  }

  private _postError(err: any): Observable<GroupModel[]> {

    const errorMessage: string = err?.status && err?.status === 409 ?
      this._translateService.instant('inviteRespondentsErrorDuplicateGroupName'):
      this._translateService.instant('errorMessageGenericBackend');

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'DeleteGroupCommand',
      false,
      err
    );

      this._logger().error({ errorMessage: 'An error occurred attempting to restore a deleted group.', error: err});
    this._appService.setLastError(error);
    this._group.errorMessage = this._translateService.instant('v2GroupDeleteUndoerror');
    return of([this._group]);
  }

  private _createEmptyRespondentModel(
    groupId: string,
    groupIndex: number,
    isExpanded = false
  ) {
    return {
      emailAddress: '',
      firstName: '',
      lastName: '',
      groupId,
      groupIndex,
      isExpanded,
      languageId: 0,
    } as RespondentModel;
  }
}
